/**
 * HomeDashboard — the "Dashboard" tab landing view of the admin panel.
 * Renders:
 *  - WebsiteAnalyticsCard (visitatori, chat, prenotazioni)
 *  - 4 KPI cards (totali / in attesa / confermate / ricavi)
 *  - NoShowAlertBanner, LongClosedAlertBanner, Next7DaysWidget
 *  - 4 day-notification cards (check-in/out/extras/in-house)
 *  - Welcome banner + 12 quick-access tiles
 *
 * Extracted from AdminDashboardPage.jsx (iter114 refactor). Behavior is
 * unchanged — only the file location moved.
 */
import {
  Inbox, Clock, BookOpenCheck, Wallet, LogIn, LogOut as LogOutIcon,
  Bell, BedDouble, ChevronRight,
} from "lucide-react";
import WebsiteAnalyticsCard from "@/components/admin/WebsiteAnalyticsCard";
import NoShowAlertBanner from "@/components/admin/NoShowAlertBanner";
import LongClosedAlertBanner from "@/components/admin/LongClosedAlertBanner";
import Next7DaysWidget from "@/components/admin/Next7DaysWidget";
import NotifyCard from "@/components/admin/NotifyCard";
import QuickAlertsWidget from "@/components/admin/QuickAlertsWidget";
import {
  HOME_TILES, sortTilesByUsage, trackTileUse, todayISO,
} from "@/components/admin/dashboardConfig";

const HomeDashboard = ({
  navigate, setActiveTab, stats, activeProperty,
  notifications, markViewedAndGo,
  setFilterDate, setFilterDateMode, setFilterStatus, setFilterReceipt,
  bookings = [],
}) => {
  const tiles = sortTilesByUsage(HOME_TILES);
  return (
    <div className="space-y-6" data-testid="admin-home">
      <WebsiteAnalyticsCard />

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="dashboard-kpi-row">
          <NotifyCard
            icon={Inbox}
            label="Prenotazioni totali"
            value={stats.total_bookings ?? 0}
            accent="text-[#1E3A5F]"
            onClick={() => {
              setFilterDate("");
              setFilterDateMode("stay");
              setFilterStatus("all");
              setFilterReceipt("all");
              setActiveTab("bookings");
            }}
            testId="kpi-total-bookings"
          />
          <NotifyCard
            icon={Clock}
            label="In attesa di conferma"
            value={stats.pending ?? 0}
            accent="text-amber-700"
            onClick={() => {
              setFilterDate("");
              setFilterDateMode("stay");
              setFilterStatus("pending");
              setFilterReceipt("all");
              setActiveTab("bookings");
            }}
            testId="kpi-pending"
          />
          <NotifyCard
            icon={BookOpenCheck}
            label="Confermate"
            value={stats.confirmed ?? 0}
            accent="text-emerald-700"
            onClick={() => {
              setFilterDate("");
              setFilterDateMode("stay");
              setFilterStatus("confirmed");
              setFilterReceipt("all");
              setActiveTab("bookings");
            }}
            testId="kpi-confirmed"
          />
          <NotifyCard
            icon={Wallet}
            label="Ricavi confermati"
            value={`€${(stats.confirmed_revenue ?? 0).toLocaleString("it-IT", { maximumFractionDigits: 0 })}`}
            accent="text-[#AC5D45]"
            onClick={() => setActiveTab("management")}
            testId="kpi-revenue"
          />
        </div>
      )}

      {/* iter186 — Banner "Possibili no-show" e "Camere chiuse a lungo"
          RIMOSSI su richiesta dell'host: creavano notifiche persistenti
          anche dopo la gestione. Le stesse info restano visibili tramite
          gli status delle prenotazioni nel calendario. */}
      {/* iter187 — Widget collassabile che riunisce le 5 anomalie residue
          in una sola riga. Chiuso di default; se cliccato mostra il
          breakdown. Torna verde "Tutto in ordine" quando total=0. */}
      <QuickAlertsWidget
        setActiveTab={setActiveTab}
        setFilterStatus={setFilterStatus}
      />
      <Next7DaysWidget
        setActiveTab={setActiveTab}
        setFilterDate={setFilterDate}
        setFilterDateMode={setFilterDateMode}
        setFilterStatus={setFilterStatus}
      />

      {notifications && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="admin-notify">
          <NotifyCard
            icon={LogIn}
            label="Check-in oggi"
            value={notifications.checkin.count}
            accent="text-emerald-700"
            hasNew={notifications.checkin.hasNew}
            onClick={() => markViewedAndGo("nv_checkin", "checkins", () => {
              try {
                const url = new URL(window.location.href);
                url.searchParams.set("checkins_filter", "today");
                window.history.replaceState({}, "", url.toString());
              } catch { /* noop */ }
            })}
            testId="notify-checkin"
          />
          <NotifyCard
            icon={LogOutIcon}
            label="Check-out oggi"
            value={notifications.checkout.count}
            accent="text-sky-700"
            hasNew={notifications.checkout.hasNew}
            onClick={() => markViewedAndGo("nv_checkout", "bookings", () => {
              const today = todayISO();
              setFilterDate(today);
              setFilterDateMode("checkout");
              setFilterStatus("all");
              setFilterReceipt("all");
            })}
            testId="notify-checkout"
          />
          <NotifyCard
            icon={Bell}
            label="Richieste extra"
            value={notifications.extras.count}
            accent="text-[#AC5D45]"
            hasNew={notifications.extras.hasNew}
            onClick={() => markViewedAndGo("nv_extras", "shuttle")}
            testId="notify-extras"
          />
          <NotifyCard
            icon={BedDouble}
            label="In casa adesso"
            value={(() => {
              const t = todayISO();
              return bookings.filter((b) => {
                if (!b.check_in || !b.check_out) return false;
                if (b.check_in > t || b.check_out < t) return false;
                // Solo ospiti che hanno fisicamente fatto check-in.
                // NON includere "confirmed" — quello è solo prenotazione
                // confermata, l'ospite potrebbe non essere ancora arrivato.
                return b.status === "checked_in";
              }).length;
            })()}
            accent="text-blue-700"
            onClick={() => {
              setActiveTab("checkins");
              try {
                const url = new URL(window.location.href);
                url.searchParams.set("checkins_filter", "in_house");
                window.history.replaceState({}, "", url.toString());
              } catch (e) { /* noop */ }
            }}
            testId="notify-in-house"
          />
        </div>
      )}

      <div className="bg-gradient-to-br from-[#1E3A5F] to-[#3A5278] text-white rounded-2xl p-6 md:p-8">
        <h2 className="font-display text-2xl md:text-3xl mb-1">
          Benvenuto nel pannello {activeProperty?.name || "B&B Emmanuel"}
        </h2>
        <p className="text-white/75 text-sm">
          Scegli una sezione per iniziare. I riquadri qui sotto sono ordinati in base alle funzioni che usi più spesso.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="home-tiles">
        {tiles.map((tile) => {
          const Icon = tile.icon;
          const onClick = () => {
            trackTileUse(tile.id);
            if (tile.path) navigate(tile.path);
            else setActiveTab(tile.id);
          };
          return (
            <button
              key={tile.id}
              type="button"
              onClick={onClick}
              data-testid={`home-tile-${tile.id}`}
              className="group relative overflow-hidden text-left rounded-2xl p-5 bg-white border border-[#E8E2D9] hover:border-[#AC5D45] hover:shadow-lg transition-all"
            >
              <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br ${tile.color} text-white shadow-md mb-3 group-hover:scale-110 transition-transform`}>
                <Icon className="w-6 h-6" />
              </div>
              <div className="font-display text-lg text-[#1E3A5F]">{tile.label}</div>
              <div className="text-sm text-[#635F59] mt-1">{tile.desc}</div>
              <ChevronRight className="absolute top-5 right-5 w-5 h-5 text-[#E8E2D9] group-hover:text-[#AC5D45] group-hover:translate-x-1 transition" />
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default HomeDashboard;
